import * as React from 'react';

import Loader from '../Components/Loader';
import { SideTab } from '../Components/SideTab';
import { AutomationRepository } from '../../MedicareProAPI/Repositories/Automation/AutomationRepository';
import { AutomationActionRepository } from '../../MedicareProAPI/Repositories/Automation/AutomationActionRepository';
import { AutomationAction } from '../../MedicareProAPI/Entities/Automation/AutomationAction';
import { AutomationActionForm } from '../Components/Automation/Action/AutomationActionForm';
import { Automation } from '../../MedicareProAPI/Entities/Automation/Automation';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import { AutomationScheduleRepository } from '../../MedicareProAPI/Repositories/Automation/AutomationScheduleRepository';
import { AutomationScheduleCalendarItem } from '../Components/Automation/Schedule/AutomationScheduleCalendarItem';
import type { AutomationSchedule } from '../../MedicareProAPI/Entities/Automation/AutomationSchedule';

export interface AutomationCalendarScheduleGroup {
  id: string;
  schedules: AutomationSchedule[];
}

export interface AutomationCalendarAutomation {
  id: string;
  automationName: string;
  groupedSchedules: AutomationCalendarScheduleGroup[];
}

export interface AutomationCalendarDay {
  id: string;
  automations: AutomationCalendarAutomation[];
}

export default function AgentAutomationSchedulePanel(props) {
  const [scheduledItemSelected, setScheduledItemSelected] = React.useState<number>(null);
  const [scheduledItems, setScheduleItems] = React.useState<AutomationCalendarDay[]>(null);
  const [scheduledDays, setScheduleDays] = React.useState([]);
  const [scheduledItemsLoading, setScheduleItemsLoading] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = React.useState(new Date().getMonth());

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const automationScheduleRepository = new AutomationScheduleRepository();
  automationScheduleRepository.setAccessToken(props.userAccessToken);

  const nextMonth = () => {
    const newDate = new Date(selectedYear, selectedMonth, 1);
    newDate.setMonth(newDate.getMonth() + 1);
    const newMonth = newDate.getMonth();
    const newYear = newDate.getFullYear();

    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const prevMonth = () => {
    const newDate = new Date(selectedYear, selectedMonth, 1);
    newDate.setMonth(newDate.getMonth() - 1);
    const newMonth = newDate.getMonth();
    const newYear = newDate.getFullYear();

    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  React.useEffect(() => {
    // Create the calendar date range
    // Monday is the first day of the week
    const startDay = new Date(selectedYear, selectedMonth, 1, 0, 0, 0);

    startDay.setDate(startDay.getDate() - ((startDay.getDay() + 6) % 7));

    // Sunday is the last day of the week
    const endDay = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59);

    endDay.setDate(endDay.getDate() + ((7 - 1 - endDay.getDay() + 7) % 7) + 1);

    const dateCursor = new Date(startDay);
    const newScheduleDays = [];

    while (dateCursor <= endDay) {
      const currentDate = new Date(dateCursor);
      newScheduleDays.push(currentDate);
      dateCursor.setDate(dateCursor.getDate() + 1);
    }

    setScheduleDays(newScheduleDays);

    const newScheduleItems: AutomationCalendarDay[] = [];

    automationScheduleRepository
      .getAutomationSchedules(props.userId, {
        criteria: {
          scheduledDateTime: {
            from: Utilities.dateTimeToDateTimeString(startDay),
            to: Utilities.dateTimeToDateTimeString(endDay),
          },
        },
        include: ['automationAction.automation'],
      })
      .then((schedules) => {
        schedules.getItems().forEach((singleSchedule) => {
          const dayKey =
            singleSchedule.getScheduledDateTime().getFullYear() +
            '-' +
            singleSchedule.getScheduledDateTime().getMonth() +
            '-' +
            singleSchedule.getScheduledDateTime().getDate();
          const automationKey = singleSchedule.getAutomationAction().getAutomation().getId();
          const scheduleGroupKey = singleSchedule.getGroupKey();

          // Represents a single day in the calendar
          let existingDay: AutomationCalendarDay;

          existingDay = newScheduleItems.find((newScheduleItem) => newScheduleItem.id === dayKey);

          if (!existingDay) {
            existingDay = {
              id: dayKey,
              automations: [],
            };

            newScheduleItems.push(existingDay);
          }

          // Each day can have multiple items scheduled; we group them by the action they belong to
          let existingAutomation: AutomationCalendarAutomation;

          existingAutomation = existingDay.automations.find(
            (existingDayItem) => existingDayItem.id === automationKey
          );

          if (!existingAutomation) {
            existingAutomation = {
              id: automationKey,
              automationName: singleSchedule.getAutomationAction().getAutomation().getName(),
              groupedSchedules: [],
            };

            existingDay.automations.push(existingAutomation);
          }

          // Sometimes an action will trigger more than one scheduled item based on specific criteria. We group those here.
          let existingSchedule: AutomationCalendarScheduleGroup;

          if (scheduleGroupKey) {
            existingSchedule = existingAutomation.groupedSchedules.find(
              (groupedSchedule: AutomationCalendarScheduleGroup) =>
                groupedSchedule.id === scheduleGroupKey
            );
          }

          if (!existingSchedule) {
            existingSchedule = {
              id: scheduleGroupKey,
              schedules: [],
            };

            existingAutomation.groupedSchedules.push(existingSchedule);
          }

          existingSchedule.schedules.push(singleSchedule);
        });

        setScheduleItems(newScheduleItems);
      });
  }, [selectedYear, selectedMonth]);

  return (
    <div className="card card-tall m-0 p-0">
      {/*  */}
      <div className="card-body m-0 p-0">
        {scheduledItemsLoading && <Loader size="large" className="mt-4 mb-4" />}
        {!scheduledItemsLoading && (
          <>
            <div className="row m-0">
              <div className="col-12 pt-2 pb-2 text-center">
                <button className="btn" onClick={prevMonth}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <>
                  {' '}
                  {months[selectedMonth]} {selectedYear}{' '}
                </>
                <button className="btn" onClick={nextMonth}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
            <div className="row m-0">
              {scheduledDays.length > 0 &&
                scheduledDays.slice(0, 7).map((dayItem) => {
                  const classList = ['col', 'p-3'];
                  if (dayItem.getDay() !== 7) {
                    classList.push('border-left');
                  }
                  return <div className={classList.join(' ')}>{daysOfWeek[dayItem.getDay()]}</div>;
                })}
            </div>
            {scheduledDays.length > 0 &&
              Utilities.chunk(scheduledDays, 7).map((week) => {
                return (
                  <div className="row m-0">
                    {week.map((day) => {
                      const dayKey = day.getFullYear() + '-' + day.getMonth() + '-' + day.getDate();
                      const scheduleDay = scheduledItems
                        ? scheduledItems.find((scheduledItem) => dayKey === scheduledItem.id)
                        : null;

                      const classList = ['col', 'p-3', 'calendar-day', 'border-top'];

                      if (day.getDay() !== 7) {
                        classList.push('border-left');
                      }

                      return (
                        <div className={classList.join(' ')}>
                          <div>{day.getDate()}</div>
                          {scheduleDay && scheduleDay.automations.length && (
                            <div>
                              {scheduleDay.automations.map(
                                (scheduleAutomation: AutomationCalendarAutomation) => {
                                  return (
                                    <AutomationScheduleCalendarItem
                                      scheduleItem={scheduleAutomation}
                                      userAccessToken={props.userAccessToken}
                                      userId={props.userId}
                                    />
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </>
        )}
      </div>
    </div>
  );
}
